import { timeout, UnixDuration } from '@sparkdotfi/common-universal'
import { QueryKey, UseSuspenseQueryResult, useSuspenseQuery } from '@tanstack/react-query'
import { Address, MethodNotFoundRpcError, MethodNotSupportedRpcError } from 'viem'
import { Config, Connector, useChainId, useConfig } from 'wagmi'
import { getCapabilities } from 'wagmi/actions'

import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

const CAPABILITIES_QUERY_FN_TIMEOUT = UnixDuration({ seconds: 3 })

export interface CanWalletBatchResult {
  canWalletBatch: boolean
}

export function useCanWalletBatch(): UseSuspenseQueryResult<CanWalletBatchResult> {
  const config = useConfig()
  const chainId = useChainId()
  const { account, connector } = useConnectedAddress({ throwOnNotConnected: false })
  return useSuspenseQuery({
    queryKey: canWalletBatchQueryKey({ account, chainId, connector }),
    queryFn: async () =>
      await canWalletBatchQueryFn(
        { account, chainId, config },
        { throwOnError: false, fallbackValue: { canWalletBatch: false } }, // results in no refetching
      ),
  })
}

export interface CanWalletBatchQueryKeyParams {
  account: Address | undefined
  chainId: number | undefined
  connector: Connector | undefined
}

export function canWalletBatchQueryKey({ account, chainId, connector }: CanWalletBatchQueryKeyParams): QueryKey {
  return ['can-wallet-batch', account, chainId, connector?.id]
}

interface CanWalletBatchQueryFnParams {
  account: Address | undefined
  chainId: number | undefined
  config: Config
}
type CanWalletBatchQueryFnOptions =
  | {
      throwOnError: false
      fallbackValue: CanWalletBatchResult
    }
  | {
      throwOnError: true
    }

export async function canWalletBatchQueryFn(
  { account, chainId, config }: CanWalletBatchQueryFnParams,
  options: CanWalletBatchQueryFnOptions,
): Promise<CanWalletBatchResult> {
  if (!account || !chainId) {
    return { canWalletBatch: false }
  }
  try {
    const capabilities = await timeout({
      fn: () => getCapabilities(config, { account }),
      timeout: CAPABILITIES_QUERY_FN_TIMEOUT,
    })
    const chainCapabilities = capabilities[chainId]

    const canWalletBatchV1 = chainCapabilities?.atomicBatch?.supported === true
    const atomicStatus = chainCapabilities?.atomic?.status
    const canWalletBatchV2 = atomicStatus === 'supported' || atomicStatus === 'ready'

    return { canWalletBatch: canWalletBatchV1 || canWalletBatchV2 }
  } catch (e) {
    if (e instanceof MethodNotFoundRpcError || e instanceof MethodNotSupportedRpcError) {
      return { canWalletBatch: false }
    }
    if (options.throwOnError) {
      throw e
    }
    return options.fallbackValue
  }
}
